import axios from '../config/axiosConfig';

const API_URL = '/siem';

// Get all SIEM integrations
const getIntegrations = async () => {
  const response = await axios.get(API_URL);
  return response.data;
};

// Get single integration
const getIntegration = async (id) => {
  const response = await axios.get(`${API_URL}/${id}`);
  return response.data;
};

// Create new integration
const createIntegration = async (integrationData) => {
  const response = await axios.post(API_URL, integrationData);
  return response.data;
};

// Update integration
const updateIntegration = async (id, updates) => {
  const response = await axios.put(`${API_URL}/${id}`, updates);
  return response.data;
};

// Delete integration
const deleteIntegration = async (id) => {
  const response = await axios.delete(`${API_URL}/${id}`);
  return response.data;
};

// Test integration connection
const testIntegration = async (id) => {
  const response = await axios.post(`${API_URL}/${id}/test`);
  return response.data;
};

// Toggle integration (enable/disable)
const toggleIntegration = async (id) => {
  const response = await axios.post(`${API_URL}/${id}/toggle`);
  return response.data;
};

// Send events to SIEM
const sendEvents = async (events, eventType = 'actions') => {
  const response = await axios.post(`${API_URL}/send`, { events, eventType });
  return response.data;
};

// Get event logs for an integration
const getEventLogs = async (integrationId, options = {}) => {
  const params = new URLSearchParams();
  if (options.status) params.append('status', options.status);
  if (options.limit) params.append('limit', options.limit);
  if (options.skip) params.append('skip', options.skip);

  const response = await axios.get(`${API_URL}/${integrationId}/logs?${params.toString()}`);
  return response.data;
};

// Get supported SIEM types
const getSIEMTypes = async () => {
  const response = await axios.get(`${API_URL}/types`);
  return response.data;
};

// Get SIEM statistics
const getStats = async () => {
  const response = await axios.get(`${API_URL}/stats`);
  return response.data;
};

// SIEM type configurations (for forms)
const siemTypeConfigs = {
  splunk: {
    name: 'Splunk',
    icon: 'chart-line',
    color: '#65A637',
    fields: [
      { key: 'splunkConfig.hecEndpoint', label: 'HEC Endpoint', type: 'url', required: true, placeholder: 'https://splunk.example.com:8088' },
      { key: 'splunkConfig.hecToken', label: 'HEC Token', type: 'password', required: true },
      { key: 'splunkConfig.index', label: 'Index', type: 'text', required: false, default: 'main' },
      { key: 'splunkConfig.sourcetype', label: 'Sourcetype', type: 'text', required: false, default: 'zerberos:event' },
      { key: 'splunkConfig.source', label: 'Source', type: 'text', required: false, default: 'zerberos' }
    ],
    queryFields: [
      { key: 'queryConfig.splunkSearchEndpoint', label: 'Search API Endpoint', type: 'url', placeholder: 'https://splunk.example.com:8089' },
      { key: 'queryConfig.splunkSearchToken', label: 'Search Token', type: 'password' },
      { key: 'queryConfig.splunkDefaultIndex', label: 'Default Search Index', type: 'text', default: 'main' }
    ]
  },
  elastic: {
    name: 'Elastic Security',
    icon: 'search',
    color: '#00BFB3',
    fields: [
      { key: 'elasticConfig.endpoint', label: 'Elasticsearch Endpoint', type: 'url', required: true, placeholder: 'https://elastic.example.com:9200' },
      { key: 'elasticConfig.apiKey', label: 'API Key', type: 'password', required: true },
      { key: 'elasticConfig.index', label: 'Index', type: 'text', required: false, default: 'zerberos-events' },
      { key: 'elasticConfig.pipeline', label: 'Ingest Pipeline', type: 'text', required: false }
    ],
    queryFields: [
      { key: 'queryConfig.elasticSearchEndpoint', label: 'Search Endpoint', type: 'url', placeholder: 'https://elastic.example.com:9200' },
      { key: 'queryConfig.elasticSearchIndex', label: 'Search Index Pattern', type: 'text', default: 'filebeat-*,winlogbeat-*' },
      { key: 'queryConfig.elasticSearchApiKey', label: 'Search API Key', type: 'password' }
    ]
  },
  microsoft_sentinel: {
    name: 'Microsoft Sentinel',
    icon: 'shield-alt',
    color: '#0078D4',
    fields: [
      { key: 'sentinelConfig.workspaceId', label: 'Workspace ID', type: 'text', required: true },
      { key: 'sentinelConfig.sharedKey', label: 'Shared Key', type: 'password', required: true },
      { key: 'sentinelConfig.logType', label: 'Log Type', type: 'text', required: false, default: 'ZerberosEvent' }
    ],
    queryFields: [
      { key: 'queryConfig.sentinelTenantId', label: 'Azure AD Tenant ID', type: 'text' },
      { key: 'queryConfig.sentinelClientId', label: 'App Registration Client ID', type: 'text' },
      { key: 'queryConfig.sentinelClientSecret', label: 'App Registration Client Secret', type: 'password' }
    ]
  },
  wazuh: {
    name: 'Wazuh',
    icon: 'shield-alt',
    color: '#0066CC',
    fields: [
      { key: 'wazuhConfig.managerUrl', label: 'Wazuh Manager URL', type: 'url', required: true, placeholder: 'https://wazuh-manager:55000' },
      { key: 'wazuhConfig.apiUser', label: 'API Username', type: 'text', required: true },
      { key: 'wazuhConfig.apiPassword', label: 'API Password', type: 'password', required: true },
      { key: 'wazuhConfig.indexPattern', label: 'Index Pattern', type: 'text', required: false, default: 'wazuh-alerts-*' },
      { key: 'wazuhConfig.agentId', label: 'Agent ID (optional)', type: 'text', required: false },
      { key: 'wazuhConfig.verifySSL', label: 'Verify SSL', type: 'checkbox', required: false, default: true }
    ],
    queryFields: [
      { key: 'queryConfig.wazuhAlertsEndpoint', label: 'Alerts Endpoint', type: 'text', default: '/alerts', placeholder: '/alerts' }
    ]
  },
  crowdstrike: {
    name: 'CrowdStrike',
    icon: 'shield-alt',
    color: '#E01E2F',
    fields: [
      { key: 'crowdstrikeConfig.baseUrl', label: 'API Base URL', type: 'url', required: true, placeholder: 'https://api.crowdstrike.com' },
      { key: 'crowdstrikeConfig.clientId', label: 'Client ID', type: 'text', required: true },
      { key: 'crowdstrikeConfig.clientSecret', label: 'Client Secret', type: 'password', required: true }
    ],
    queryFields: [
      { key: 'queryConfig.crowdstrikeBaseUrl', label: 'Query API Base URL', type: 'url', placeholder: 'https://api.crowdstrike.com' },
      { key: 'queryConfig.crowdstrikeClientId', label: 'Query Client ID', type: 'text' },
      { key: 'queryConfig.crowdstrikeClientSecret', label: 'Query Client Secret', type: 'password' }
    ]
  },
  syslog: {
    name: 'Syslog',
    icon: 'server',
    color: '#6B7280',
    fields: [
      { key: 'syslogConfig.host', label: 'Syslog Host', type: 'text', required: true },
      { key: 'syslogConfig.port', label: 'Port', type: 'number', required: true, default: 514 },
      { key: 'syslogConfig.protocol', label: 'Protocol', type: 'select', options: ['tcp', 'udp', 'tls'], required: true, default: 'tcp' },
      { key: 'syslogConfig.facility', label: 'Facility', type: 'number', required: false, default: 1 }
    ]
  },
  webhook: {
    name: 'Webhook',
    icon: 'globe',
    color: '#8B5CF6',
    fields: [
      { key: 'webhookConfig.url', label: 'Webhook URL', type: 'url', required: true },
      { key: 'webhookConfig.method', label: 'HTTP Method', type: 'select', options: ['POST', 'PUT'], required: false, default: 'POST' },
      { key: 'webhookConfig.authType', label: 'Authentication', type: 'select', options: ['none', 'basic', 'bearer', 'api_key'], required: false, default: 'none' },
      { key: 'webhookConfig.authValue', label: 'Auth Value/Token', type: 'password', required: false }
    ]
  }
};

// Health status info
const healthStatusInfo = {
  healthy: { label: 'Healthy', color: 'green', icon: 'check-circle' },
  degraded: { label: 'Degraded', color: 'yellow', icon: 'exclamation-circle' },
  unhealthy: { label: 'Unhealthy', color: 'red', icon: 'times-circle' },
  unknown: { label: 'Unknown', color: 'gray', icon: 'question-circle' }
};

// Get config for a SIEM type
const getTypeConfig = (type) => {
  return siemTypeConfigs[type] || siemTypeConfigs.webhook;
};

// Get health status info
const getHealthInfo = (status) => {
  return healthStatusInfo[status] || healthStatusInfo.unknown;
};

const siemService = {
  getIntegrations,
  getIntegration,
  createIntegration,
  updateIntegration,
  deleteIntegration,
  testIntegration,
  toggleIntegration,
  sendEvents,
  getEventLogs,
  getSIEMTypes,
  getStats,
  siemTypeConfigs,
  healthStatusInfo,
  getTypeConfig,
  getHealthInfo
};

export default siemService;
